import { useEffect, useRef, useState } from 'react';
import type { Project, Equipment, AMIDataPoint } from '../types/project';
import { validateProjectForCompletion } from '../validation/projectCompletionValidation';
import { computeProgress, emptyProgress, getProgressItems, type ProjectProgress } from '../lib/projectProgress';

/**
 * Live completion progress for the header strip and tab dots.
 *
 * Runs the real completion validator rather than a parallel rule set, so the
 * strip and the Mark Complete gate can never disagree. The validator walks the
 * whole AMI dataset to measure its span — tens of thousands of points on a real
 * upload — so edits are debounced instead of recomputing on every keystroke.
 * The first run for a project is immediate: a loaded project should not flash
 * 0% before settling.
 */
export function useProjectProgress(
  projectId: string | null,
  project: Partial<Project>,
  equipment: Equipment[],
  amiData: AMIDataPoint[],
  debounceMs = 150,
): ProjectProgress {
  const [progress, setProgress] = useState<ProjectProgress>(emptyProgress);
  // Bumped on every scheduled run so a slow one can't overwrite a newer result.
  const runRef = useRef(0);
  const settledForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setProgress(emptyProgress);
      settledForRef.current = null;
      return;
    }

    const run = ++runRef.current;
    const immediate = settledForRef.current !== projectId;

    const compute = () => {
      validateProjectForCompletion(projectId, project, equipment, amiData)
        .then(result => {
          if (run !== runRef.current) return;
          settledForRef.current = projectId;
          setProgress(
            computeProgress(
              result.blockers.map(b => b.id),
              getProgressItems(project.report_method)
            )
          );
        })
        .catch(() => {
          // A validation failure must not take the header down with it.
        });
    };

    if (immediate) {
      compute();
      return;
    }

    const timer = setTimeout(compute, debounceMs);
    return () => clearTimeout(timer);
  }, [projectId, project, equipment, amiData, debounceMs]);

  return progress;
}
